import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, IconButton, PopoverOrigin, Theme, Typography } from '@mui/material';
import { SxProps, SystemStyleObject } from '@mui/system';
import React, { ChangeEvent, MouseEvent, ReactNode, useRef, useState } from 'react';
import { List } from './components';
import { EntityPopover } from 'ui-component/records/RecordGrid/components/EntityPopover';
import { Entity } from 'ui-component/icons/Entity';

export type GridSingleSelectProps = {
    options: GridSingleSelectOption[];
    value: number | string;
    onChange: (newId: number | string) => void;
    leftButtonLabel?: ReactNode;
    leftIcon?: ReactNode;
    listFitContent?: boolean;
    width?: string;
    error?: boolean;
    sx?: SxProps;
    disabled?: boolean;
    PopoverSx?: SxProps<Theme> | undefined;
    PopoverPropsAnchorOrigin?: PopoverOrigin | undefined;
    PopoverPropsTransformOrigin?: PopoverOrigin | undefined;
    autoWidth?: boolean;
    selectedOptionSx?: SystemStyleObject;
    isEntity?: boolean;
};

export type GridSingleSelectOption = {
    id: number | string;
    name: string;
    key?: string;
    renderName?: ReactNode;
    valuesToFilter?: string[];
};

export const GridSingleSelect = ({
    options,
    value,
    onChange,
    leftButtonLabel,
    leftIcon,
    listFitContent,
    width,
    error,
    sx,
    disabled,
    PopoverSx,
    PopoverPropsAnchorOrigin,
    PopoverPropsTransformOrigin,
    autoWidth,
    selectedOptionSx = {},
    isEntity
}: GridSingleSelectProps) => {
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchText, setSearchText] = useState('');
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
    const [currentEntityId, setCurrentEntityId] = useState<number | string | null>(null);

    const filteredOptions = options.filter(
        (el) =>
            el.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            el.valuesToFilter?.some((val) => val.toLowerCase().includes(searchText.toLowerCase()))
    );
    const open = Boolean(anchorEl);

    const selectedOption = value ? options.find((el) => el.id === value) || { name: '', id: undefined } : null;

    const handleOpenOptions = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setTimeout(() => {
            if (searchInputRef.current) searchInputRef.current.focus();
        }, 100);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        setSearchText(newVal);
    };

    const handleSelectOption = (selectedId: number | string) => () => {
        onChange(selectedId);
        handleClose();
    };

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, entityId: number | string) => {
        setPopoverAnchorEl(event.currentTarget);
        setCurrentEntityId(entityId);
    };

    const handleMouseLeave = () => {
        setTimeout(() => {
            setPopoverAnchorEl(null);
            setCurrentEntityId(null);
        }, 100);
    };

    const openPopover = Boolean(popoverAnchorEl);

    return (
        <Box
            ref={containerRef}
            sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                ...(!autoWidth && { width: width || '200px' }),
                outline: error ? 'solid 1px #EF5350' : 'none',
                ...sx
            }}
        >
            {leftButtonLabel || (
                <Box display="flex" alignItems="center" justifyContent="space-between" width="90%">
                    <Typography
                        fontSize="14px"
                        sx={{
                            m: 0,
                            maxHeight: '100%',
                            maxWidth: '90%',
                            flexGrow: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            p: '0.25rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            ...selectedOptionSx
                        }}
                    >
                        {selectedOption?.name ?? ''}
                    </Typography>
                    {isEntity && selectedOption?.id && (
                        <Box
                            data-entity-key={`entity-${selectedOption?.name}-${selectedOption?.id}`}
                            onMouseEnter={(event) => handleMouseEnter(event, selectedOption?.id)}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Entity sx={{ width: '1rem', height: '1rem', cursor: 'pointer' }} />
                            <EntityPopover
                                isDropdown
                                entityId={selectedOption?.id}
                                open={openPopover && currentEntityId === selectedOption?.id}
                                anchorEl={popoverAnchorEl}
                                onClose={handleMouseLeave}
                            />
                        </Box>
                    )}
                </Box>
            )}
            <IconButton disableRipple disabled={disabled} onClick={handleOpenOptions} sx={{ p: 0, m: 0 }}>
                {leftIcon || (open ? <ExpandLess htmlColor="#54595E" /> : <ExpandMore htmlColor="#54595E" />)}
            </IconButton>
            <List
                open={open}
                anchorEl={anchorEl}
                searchInputRef={searchInputRef}
                containerRef={containerRef}
                searchText={searchText}
                options={filteredOptions}
                onClose={handleClose}
                onChangeSearch={handleChangeSearch}
                onSelectOption={handleSelectOption}
                onClearSearch={() => setSearchText('')}
                fitContent={listFitContent}
                PopoverSx={PopoverSx}
                PopoverPropsAnchorOrigin={PopoverPropsAnchorOrigin}
                PopoverPropsTransformOrigin={PopoverPropsTransformOrigin}
                isEntity={isEntity}
            />
        </Box>
    );
};
